<template>
  <b-sidebar
    id="manage-certificate-types"
    sidebar-class="sidebar-lg"
    :visible="sidebar.visible"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
    @shown="onShow"
    @change="val => mutateSidebar({ visible: val })"
  >
    <template #default="{ hide }">

      <!-- HEADER -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h4>Gerenciar Cursos</h4>

        <feather-icon
          class="cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- LOADING -->
      <div v-if="loading" class="text-center py-3">
        <b-spinner variant="primary" />
      </div>

      <!-- CONTENT -->
      <div v-else class="p-2">

        <!-- SELECT -->
        <b-form-group label="Selecione o curso" class="mt-1">
          <v-select
            v-model="selectedCourse"
            :options="certificateTypes"
            label="name"
            placeholder="Escolha o curso"
            :reduce="c => c"
            @input="onSelect"
          />
        </b-form-group>

        <!-- FORM FIELDS -->
        <div v-if="form" class="mt-2">

          <!-- Nome -->
          <b-form-group label="Nome do curso" class="mt-1">
            <b-form-input v-model="form.name" />
          </b-form-group>

          <!-- Carga horária -->
          <b-form-group label="Carga horária total (horas)" class="mt-1">
            <b-form-input
              v-model.number="form.workload_hours"
              type="number"
              min="0"
              placeholder="Ex.: 252"
            />
            <small class="text-warning d-block mt-50">
              No certificado: "Com carga horária de
              <strong>{{ form.workload_hours || "XXX" }}</strong> horas no dia DD/MM/AAAA"
            </small>
          </b-form-group>

          <!-- Selos institucionais -->
          <b-form-group label="Selos institucionais" class="mt-1">
            <b-form-checkbox v-model="form.has_planejar_seal" class="mb-50">
              Selo Planejar pass
            </b-form-checkbox>

            <b-form-checkbox v-model="form.has_pec_ancord_seal">
              Selo PEC Ancord
            </b-form-checkbox>

            <small class="text-warning d-block mt-50">
              Só marque para cursos que realmente possuem a qualificação. Sem marcar, o certificado sai sem selo.
            </small>
          </b-form-group>

          <!-- Descrição -->
          <b-form-group label="Descrição" class="mt-1">
            <b-form-textarea
              v-model="form.description"
              rows="3"
            />
          </b-form-group>

          <!-- Status -->
          <b-form-group label="Status" class="mt-1">
            <v-select
              v-model="form.is_active"
              :options="[
                { label: 'Ativo', value: true },
                { label: 'Desativado', value: false }
              ]"
              :reduce="o => o.value"
              label="label"
              placeholder="Selecione o status"
            />
          </b-form-group>

          <!-- AÇÕES -->
          <div class="d-flex mt-2 justify-content-end">

            <!-- Botão Atualizar (só habilita se algo mudou) -->
            <b-button
              variant="primary"
              class="certificate-buttons"
              :disabled="!isDirty"
              @click="updateCourse"
            >
              Atualizar
            </b-button>

          </div>

        </div>
      </div>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BSpinner,
} from "bootstrap-vue";

import vSelect from "vue-select";
import { mapGetters, mapActions, mapMutations } from "vuex";
import * as types from "../store/types";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";


export default {
  components: {
    BSidebar,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BSpinner,
    vSelect,
  },

    setup() {
        return {
            toast: useToast(),
        };
    },

  data() {
    return {
      loading: false,
      selectedCourse: null,
      form: null,
    };
  },

  computed: {
    ...mapGetters({
      sidebar: types.MANAGE_CERTIFICATE_TYPES_SIDEBAR,
      certificateTypes: types.CERTIFICATE_TYPES,
    }),

    isDirty() {
      if (!this.form || !this.selectedCourse) return false;

      return this.form.name !== this.selectedCourse.name
        || this.form.description !== this.selectedCourse.description
        || this.form.workload_hours !== this.selectedCourse.workload_hours
        || this.form.has_planejar_seal !== this.selectedCourse.has_planejar_seal
        || this.form.has_pec_ancord_seal !== this.selectedCourse.has_pec_ancord_seal
        || this.form.is_active !== this.selectedCourse.is_active;
    },
  },

  methods: {
    ...mapMutations({
      mutateSidebar: types.MUTATE_MANAGE_CERTIFICATE_TYPES_SIDEBAR,
    }),

    ...mapActions({
      updateCourseAction: types.UPDATE_CERTIFICATE_TYPE,
      reloadTypes: types.GET_CERTIFICATE_TYPES,
    }),

    async onShow() {
      this.loading = true;
      this.selectedCourse = null;
      this.form = null;

      try {
        await this.reloadTypes({ all: true });
      } catch (e) {
        console.error("Erro ao carregar cursos:", e);
      } finally {
        this.loading = false;
      }
    },

    onSelect() {
      // Clona o curso selecionado para edição, preservando o original para detectar mudanças
      this.form = this.selectedCourse ? { ...this.selectedCourse } : null;
    },

    async updateCourse() {
        this.loading = true;

        const payload = {
            id: this.form.id,
            name: this.form.name,
            description: this.form.description,
            workload_hours: this.form.workload_hours,
            has_planejar_seal: this.form.has_planejar_seal,
            has_pec_ancord_seal: this.form.has_pec_ancord_seal,
            is_active: this.form.is_active,
        };

        try {
            await this.updateCourseAction(payload);

            this.toast({
            component: ToastificationContent,
            props: {
                title: "Sucesso!",
                text: "Curso atualizado!",
                icon: "CheckIcon",
                variant: "success",
            },
            });
            await this.reloadTypes();
            this.selectedCourse = null;
            this.form = null;
            this.mutateSidebar({ visible: false });
        } catch (error) {
            this.toast({
            component: ToastificationContent,
            props: {
                title: "Erro!",
                text: "Erro ao atualizar o curso.",
                icon: "AlertTriangleIcon",
                variant: "danger",
            },
            });
        } finally {
            this.loading = false;
        }
    },
  },
};
</script>

<style scoped>
.certificate-buttons {
  width: 11rem;
}
</style>
